import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { usePrevious } from '@react-hookz/web';
import { useBreakpointUp } from '@travel-frontend/core';
import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { once } from 'lodash';
import { useTranslation } from 'next-i18next';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { DateRangePicker } from '../../../../components/internal/DateRangePicker/index.js';
import { FlyOut } from '../../../../components/internal/FlyOut/index.js';
import { Container } from '../../../../components/SearchForm/components/TravelPeriodWidget/components/Container.js';
import { Content } from '../../../../components/SearchForm/components/TravelPeriodWidget/components/Content.js';
import { DurationSelect } from '../../../../components/SearchForm/components/TravelPeriodWidget/components/DurationSelect.js';
import { Head } from '../../../../components/SearchForm/components/TravelPeriodWidget/components/Head.js';
import { TravelDate } from '../../../../components/SearchForm/components/TravelPeriodWidget/components/TravelDate/index.js';
import { WidgetOverlay } from '../../../../components/SearchForm/components/WidgetOverlay/index.js';
import { Button } from '../../../../primitives/index.js';
import { qaId } from '../../../../utils/index.js';
import { SEARCH_FORM_QA_ID } from '../../../../utils/qaId.js';
export const TravelPeriodWidget = forwardRef(function TravelPeriodWidget({
  type,
  open,
  data,
  dimming,
  rangePending,
  handlers: {
    onDurationChange,
    onApply,
    onClose,
    onRangeChange
  },
  config: {
    minDate,
    maxDate,
    durationOptions,
    durationLimit
  },
  layout,
  withoutDuration,
  ...restProps
}, ref) {
  const {
    t
  } = useTranslation();
  const mdUp = useBreakpointUp('md');
  const isPopover = type === 'popover';
  const isDrawer = type === 'drawer';
  const {
    startDate,
    endDate,
    duration
  } = data || {};
  const durationSelectRef = useRef();
  const wasPending = usePrevious(rangePending);
  const startDateParsed = useMemo(() => startDate ? startOfDay(new Date(startDate)) : null, [startDate]);
  const endDateParsed = useMemo(() => endDate ? new Date(endDate) : null, [endDate]);
  const minDateParsed = useMemo(() => new Date(minDate), [minDate]);
  const maxDateParsed = useMemo(() => new Date(maxDate), [maxDate]);
  const selectedDaysCount = startDateParsed && endDateParsed ? differenceInCalendarDays(endDateParsed, startDateParsed) + 1 : 0;
  const hasRange = Boolean(startDate && endDate && startDate !== endDate);
  const [durationFlyOutVisible, setDurationFlyOutVisible] = useState(hasRange);
  const openDurationSelectOnInitialEmptySelection = useMemo(() => once(() => {
    if (!duration) {
      focusDurationSelect();
    }
  }), [duration]);
  const isDurationOptionDisabled = option => {
    const {
      min,
      max
    } = option.value;
    return min === 0 && max === 0 && selectedDaysCount > durationLimit || selectedDaysCount - 1 < min || max === min && selectedDaysCount === max;
  };
  const flashDurationSelect = () => {
    // @ts-expect-error TS(2339) FIXME: Property 'flash' does not exist on type 'never'.
    if (typeof durationSelectRef.current?.flash === 'function') {
      // @ts-expect-error TS(2339) FIXME: Property 'flash' does not exist on type 'never'.
      durationSelectRef.current.flash();
    }
  };
  const focusDurationSelect = () => {
    // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'never'.
    if (typeof durationSelectRef.current?.focus === 'function') {
      // @ts-expect-error TS(2339) FIXME: Property 'focus' does not exist on type 'never'.
      durationSelectRef.current.focus();
    }
  };
  useEffect(() => {
    if (hasRange && wasPending) {
      setDurationFlyOutVisible(true);
      flashDurationSelect();
    }
  }, [hasRange, wasPending]);
  const headElement = _jsxs(Head, {
    direction: "row",
    spacing: 2,
    divider: true,
    dividerProps: {
      category: 'primary'
    },
    justifyContent: isPopover ? 'space-evenly' : 'center',
    children: [_jsx(TravelDate, {
      label: t('travelPeriodWidget:label.startDate'),
      highlighted: Boolean(startDateParsed),
      children: startDateParsed ? format(startDateParsed, `EEEEEE, P`, {
        locale: de
      }) : format(new Date(), 'P', {
        locale: de
      }).replaceAll(/\d/g, '-')
    }), _jsx(TravelDate, {
      label: t('travelPeriodWidget:label.endDate'),
      highlighted: Boolean(endDateParsed && !rangePending),
      children: endDateParsed && !rangePending ? format(endDateParsed, `EEEEEE, P`, {
        locale: de
      }) : format(new Date(), 'P', {
        locale: de
      }).replaceAll(/\d/g, '-')
    })]
  });
  const durationSelect = durationOptions && !withoutDuration ? _jsx(DurationSelect, {
    ref: durationSelectRef,
    options: durationOptions,
    value: duration,
    // @ts-expect-error TS(2322) FIXME: Type '{ key: string; ref: MutableRefObject<undefin... Remove this comment to see the full error message
    isDisabled: !hasRange,
    isOptionDisabled: isDurationOptionDisabled,
    openMenuOnFocus: true,
    onChange: option => {
      onDurationChange(option.value);
    }
  }, "duration-select") : null;
  return _jsx(WidgetOverlay, {
    type: type,
    open: open,
    dim: dimming || layout === 'compact' || layout === 'sidebar',
    children: _jsxs(Container, {
      ...restProps,
      ref: ref,
      type: type,
      title: t('travelPeriodWidget:title.dialog'),
      onClose: onClose,
      controls: [mdUp ? durationSelect : null, _jsx(Button, {
        onClick: onApply,
        disabled: !startDateParsed,
        fullWidth: !mdUp,
        children: t('travelPeriodWidget:button.apply')
      }, "apply-button")],
      topControls: isDrawer && headElement,
      "data-testid": "travel-period-widget",
      children: [_jsxs(Content, {
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travelPeriodLayer'),
        spacing: "none",
        children: [!isDrawer && headElement, _jsx(DateRangePicker, {
          orientation: isPopover ? 'horizontal' : 'vertical',
          startDate: startDateParsed,
          endDate: endDateParsed,
          minDate: minDateParsed,
          maxDate: maxDateParsed,
          onChange: onRangeChange
        })]
      }), !mdUp && _jsx(FlyOut, {
        overlapping: true,
        visible: durationFlyOutVisible,
        onEntered: openDurationSelectOnInitialEmptySelection,
        children: durationSelect
      })]
    })
  });
});